import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Award,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, parseISO } from "date-fns";
import { fmtTz } from "../lib/time";

export default function AdminCertifications() {
  const { authAxios } = useAuth();
  const [certifications, setCertifications] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    employee_id: '',
    name: '',
    expiry_date: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchCertifications = useCallback(async () => {
    try {
      const [certsRes, calRes, empRes] = await Promise.all([
        authAxios.get('/certifications'),
        authAxios.get('/certifications-calendar'),
        authAxios.get('/employees')
      ]);
      setCertifications(certsRes.data);
      setCalendarData(calRes.data);
      setEmployees(empRes.data.filter(e => e.role === 'employee'));
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchCertifications();
  }, [fetchCertifications]);

  const resetForm = () => {
    setFormData({ employee_id: '', name: '', expiry_date: '', notes: '' });
    setEditingCert(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (cert) => {
    setEditingCert(cert);
    setFormData({
      employee_id: cert.employee_id,
      name: cert.name,
      expiry_date: cert.expiry_date.split('T')[0],
      notes: cert.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        expiry_date: new Date(formData.expiry_date).toISOString()
      };
      
      if (editingCert) {
        await authAxios.put(`/certifications/${editingCert.id}`, {
          name: formData.name,
          expiry_date: payload.expiry_date,
          notes: formData.notes
        });
        toast.success('Certification updated!');
      } else {
        await authAxios.post('/certifications', payload);
        toast.success('Certification added!');
      }
      setDialogOpen(false);
      resetForm();
      fetchCertifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (cert) => {
    if (!window.confirm(`Delete "${cert.name}" for ${cert.employee_name}?`)) return;
    try {
      await authAxios.delete(`/certifications/${cert.id}`);
      toast.success('Certification deleted!');
      fetchCertifications();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'expiring_soon': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'expired': return 'bg-rose-100 text-rose-800 border-rose-200';
      default: return 'bg-stone-100 text-stone-600 border-stone-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'valid': return 'Valid';
      case 'expiring_soon': return 'Expiring Soon';
      case 'expired': return 'Expired';
      default: return status;
    }
  };

  // Calendar helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const getCertsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return calendarData.filter(c => c.date === dayStr);
  };

  const prevMonth = () => setCurrentMonth(addDays(monthStart, -1));
  const nextMonth = () => setCurrentMonth(addDays(monthEnd, 1));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-certifications">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Certifications</h1>
          <p className="text-stone-500 mt-1">Track employee certifications and expiry dates</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20" data-testid="add-cert-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Certification
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">{editingCert ? 'Edit Certification' : 'Add Certification'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingCert && (
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <Select value={formData.employee_id} onValueChange={(v) => setFormData({ ...formData, employee_id: v })}>
                    <SelectTrigger data-testid="cert-employee-select" className="bg-stone-50 border-stone-200">
                      <SelectValue placeholder="Select employee..." />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="certName">Certification Name</Label>
                <Input id="certName" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="cert-name-input" className="bg-stone-50 border-stone-200" placeholder="e.g. First Aid, Forklift License, WHMIS" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input id="expiryDate" type="date" value={formData.expiry_date} onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })} required data-testid="cert-expiry-input" className="bg-stone-50 border-stone-200" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="bg-stone-50 border-stone-200" rows={2} />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500 text-white w-full" data-testid="save-cert-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : editingCert ? 'Update Certification' : 'Add Certification'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-stone-500">Valid</p>
              <p className="text-2xl font-bold text-emerald-800 font-mono">
                {certifications.filter(c => c.status === 'valid').length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-stone-500">Expiring Soon</p>
              <p className="text-2xl font-bold text-amber-800 font-mono">
                {certifications.filter(c => c.status === 'expiring_soon').length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <p className="text-sm text-stone-500">Expired</p>
              <p className="text-2xl font-bold text-rose-800 font-mono">
                {certifications.filter(c => c.status === 'expired').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="list" className="space-y-6">
        <TabsList className="bg-stone-100 p-1 rounded-xl">
          <TabsTrigger value="list" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
            <Award className="w-4 h-4 mr-2" />
            All Certifications
          </TabsTrigger>
          <TabsTrigger value="calendar" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
            <Calendar className="w-4 h-4 mr-2" />
            Calendar View
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Award className="w-5 h-5 text-stone-400" />
                Certifications ({certifications.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : certifications.length === 0 ? (
                <div className="text-center py-12 text-stone-500">
                  <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No certifications tracked yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-stone-100">
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Employee</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Certification</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Expiry Date</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {certifications.map((cert) => (
                        <TableRow key={cert.id} className={`border-stone-100 hover:bg-stone-50 ${cert.status === 'expired' ? 'bg-rose-50/50' : cert.status === 'expiring_soon' ? 'bg-amber-50/50' : ''}`}>
                          <TableCell className="font-medium">{cert.employee_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Award className="w-4 h-4 text-stone-400" />
                              {cert.name}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {fmtTz(cert.expiry_date, 'MMM d, yyyy')}
                            <span className="text-stone-400 ml-2">
                              ({cert.days_until_expiry > 0 ? `${cert.days_until_expiry}d left` : cert.days_until_expiry === 0 ? 'Today' : `${Math.abs(cert.days_until_expiry)}d ago`})
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getStatusColor(cert.status)}>
                              {getStatusLabel(cert.status)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(cert)} className="hover:bg-emerald-50" title="Renew/Edit">
                                <RefreshCw className="w-4 h-4 text-emerald-600" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(cert)} className="hover:bg-rose-50">
                                <Trash2 className="w-4 h-4 text-rose-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar">
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading">{format(currentMonth, 'MMMM yyyy')}</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={prevMonth}>Prev</Button>
                <Button variant="outline" size="sm" onClick={nextMonth}>Next</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-stone-500 py-2">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before month starts */}
                {Array.from({ length: monthStart.getDay() }).map((_, i) => (
                  <div key={`empty-${i}`} className="min-h-[80px]" />
                ))}
                {daysInMonth.map(day => {
                  const certs = getCertsForDay(day);
                  const hasExpired = certs.some(c => c.status === 'expired');
                  const hasExpiring = certs.some(c => c.status === 'expiring_soon');
                  
                  return (
                    <div key={day.toISOString()} className={`min-h-[80px] p-1 rounded-lg border ${isToday(day) ? 'border-emerald-500 bg-emerald-50' : 'border-stone-100'}`}>
                      <p className={`text-xs font-medium mb-1 ${isToday(day) ? 'text-emerald-600' : 'text-stone-600'}`}>
                        {format(day, 'd')}
                      </p>
                      <div className="space-y-1">
                        {certs.slice(0, 2).map(cert => (
                          <div key={cert.id} className={`text-xs px-1 py-0.5 rounded truncate ${cert.status === 'expired' ? 'bg-rose-100 text-rose-700' : cert.status === 'expiring_soon' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`} title={cert.title}>
                            {cert.employee_name?.split(' ')[0]}
                          </div>
                        ))}
                        {certs.length > 2 && (
                          <div className="text-xs text-stone-400">+{certs.length - 2} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-4 justify-center text-xs">
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-100" /> Valid</div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-amber-100" /> Expiring (60 days)</div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-rose-100" /> Expired</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
